// AGC: Subscription Service for DroidFarm Freemium System
// AGC: Handles subscription management, usage tracking, and limit enforcement
// AGC: Integrates with Firebase for persistence

import {
  type SubscriptionTier,
  type UserSubscription,
  type UsageTracker,
  type LimitCheckResult,
  type LimitedFeature,
  type TierLimits,
  type TierConfig,
  DEFAULT_TIER_CONFIGS,
  DEFAULT_SUBSCRIPTION,
  DEFAULT_USAGE,
  getNextTier,
} from '../types/subscription'
import { getBackendUrl } from '../utils/backendUrl'

// AGC: Backend URL for API calls — uses centralized getBackendUrl() from utils/backendUrl
const BACKEND_URL = getBackendUrl()

// AGC: localStorage key for tier configs cache
const TIER_CONFIGS_CACHE_KEY = 'droidfarm_tier_configs'

/**
 * AGC: Get current month in YYYY-MM format
 */
export function getCurrentMonth(): string {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
}

/**
 * AGC: Get user subscription from backend/Firebase
 */
export async function getUserSubscription(userId: string): Promise<UserSubscription> {
  try {
    const response = await fetch(`${BACKEND_URL}/api/v1/subscription?userId=${userId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      // AGC: Return default free subscription if not found
      if (response.status === 404) {
        console.log('📋 AGC: No subscription found, returning default free tier')
        return {
          ...DEFAULT_SUBSCRIPTION,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }
      }
      throw new Error(`Failed to fetch subscription: ${response.status}`)
    }

    const data = await response.json()
    return data.subscription
  } catch (error) {
    console.error('❌ AGC: Error fetching subscription:', error)
    // AGC: Return default free subscription on error
    return {
      ...DEFAULT_SUBSCRIPTION,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
  }
}

/**
 * AGC: Update user subscription tier
 * AGC: For now, no payment required - just tier change
 */
export async function updateSubscription(
  userId: string,
  tier: SubscriptionTier,
  billingCycle: 'monthly' | 'annual' = 'monthly'
): Promise<UserSubscription> {
  try {
    const response = await fetch(`${BACKEND_URL}/api/v1/subscription`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        tier,
        billingCycle,
      }),
    })

    if (!response.ok) {
      throw new Error(`Failed to update subscription: ${response.status}`)
    }

    const data = await response.json()
    console.log(`✅ AGC: Subscription updated to ${tier} for user ${userId}`)
    return data.subscription
  } catch (error) {
    console.error('❌ AGC: Error updating subscription:', error)
    throw error
  }
}

/**
 * AGC: Get usage tracking for current month
 */
export async function getUsageForMonth(
  userId: string,
  month: string = getCurrentMonth()
): Promise<UsageTracker> {
  try {
    const response = await fetch(
      `${BACKEND_URL}/api/v1/subscription/usage?userId=${userId}&month=${month}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )

    if (!response.ok) {
      // AGC: Return default usage if not found
      if (response.status === 404) {
        console.log('📊 AGC: No usage found for month, returning defaults')
        return {
          ...DEFAULT_USAGE,
          month,
          userId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }
      }
      throw new Error(`Failed to fetch usage: ${response.status}`)
    }

    const data = await response.json()
    return data.usage
  } catch (error) {
    console.error('❌ AGC: Error fetching usage:', error)
    // AGC: Return default usage on error
    return {
      ...DEFAULT_USAGE,
      month,
      userId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
  }
}

/**
 * AGC: Increment usage counter for a specific feature
 */
export async function incrementUsage(
  userId: string,
  feature: LimitedFeature,
  amount: number = 1
): Promise<UsageTracker> {
  try {
    const response = await fetch(`${BACKEND_URL}/api/v1/subscription/usage`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        feature,
        amount,
        month: getCurrentMonth(),
      }),
    })

    if (!response.ok) {
      throw new Error(`Failed to increment usage: ${response.status}`)
    }

    const data = await response.json()
    console.log(`📈 AGC: Usage incremented: ${feature} +${amount}`)
    return data.usage
  } catch (error) {
    console.error('❌ AGC: Error incrementing usage:', error)
    throw error
  }
}

/**
 * AGC: Map limited feature to usage tracker field and tier limit field
 */
function getFeatureMapping(feature: LimitedFeature): {
  usageField: keyof UsageTracker
  limitField: keyof TierLimits
} {
  const mapping: Record<LimitedFeature, { usageField: keyof UsageTracker; limitField: keyof TierLimits }> = {
    tasks: { usageField: 'tasksCreated', limitField: 'maxTasksPerMonth' },
    fields: { usageField: 'fieldsCreated', limitField: 'maxFields' },
    voiceCommands: { usageField: 'voiceCommandsUsed', limitField: 'maxVoiceCommandsPerMonth' },
    whatsAppAudio: { usageField: 'whatsAppAudiosProcessed', limitField: 'maxWhatsAppAudioPerMonth' },
    hectares: { usageField: 'totalHectares', limitField: 'maxHectares' },
    teamMembers: { usageField: 'currentTeamMembers', limitField: 'maxTeamMembers' },
    hectareCalculator: { usageField: 'hectareCalculatorUses', limitField: 'hectareCalculatorUsesPerMonth' },
    // AGC: Satellite imagery features
    sentinelImages: { usageField: 'sentinelImagesGenerated', limitField: 'maxSentinelImagesPerMonth' },
    planetScopeImages: { usageField: 'planetScopeImagesGenerated', limitField: 'maxSentinelImagesPerMonth' }, // AGC: Uses same limit field (null = unlimited for now)
    airbusImages: { usageField: 'airbusImagesGenerated', limitField: 'maxSentinelImagesPerMonth' }, // AGC: Uses same limit field (null = unlimited for now)
  }
  return mapping[feature]
}

/**
 * AGC: Check if user can perform an action based on limits
 * AGC: Accepts limits object directly (from Firestore tier config via context)
 */
export function checkLimit(
  limits: TierLimits,
  tier: SubscriptionTier,
  usage: UsageTracker,
  feature: LimitedFeature,
  tierConfigs?: Record<SubscriptionTier, TierConfig>
): LimitCheckResult {
  const { usageField, limitField } = getFeatureMapping(feature)

  const current = usage[usageField] as number
  const limit = limits[limitField] as number | null

  // AGC: null limit means unlimited
  if (limit === null) {
    return {
      allowed: true,
      current,
      limit: null,
      percentage: 0,
      feature,
    }
  }

  const percentage = Math.round((current / limit) * 100)
  const allowed = current < limit

  // AGC: Determine upgrade tier if not allowed
  let upgradeRequired: SubscriptionTier | undefined
  if (!allowed) {
    const nextTier = getNextTier(tier)
    if (nextTier) {
      const nextLimits = tierConfigs?.[nextTier]?.limits || DEFAULT_TIER_CONFIGS[nextTier].limits
      const nextLimit = nextLimits[limitField] as number | null
      if (nextLimit === null || nextLimit > limit) {
        upgradeRequired = nextTier
      }
    }
  }

  return {
    allowed,
    current,
    limit,
    percentage,
    feature,
    upgradeRequired,
  }
}

/**
 * AGC: Check multiple features at once
 */
export function checkAllLimits(
  limits: TierLimits,
  tier: SubscriptionTier,
  usage: UsageTracker,
  tierConfigs?: Record<SubscriptionTier, TierConfig>
): Record<LimitedFeature, LimitCheckResult> {
  const features: LimitedFeature[] = [
    'tasks',
    'fields',
    'voiceCommands',
    'whatsAppAudio',
    'hectares',
    'teamMembers',
    'hectareCalculator',
  ]

  const results: Record<LimitedFeature, LimitCheckResult> = {} as Record<LimitedFeature, LimitCheckResult>

  for (const feature of features) {
    results[feature] = checkLimit(limits, tier, usage, feature, tierConfigs)
  }

  return results
}

/**
 * AGC: Check if a boolean feature is available — reads from provided limits
 */
export function hasFeature(limits: TierLimits, feature: keyof TierLimits): boolean {
  const value = limits[feature]
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value > 0
  return true // AGC: null means unlimited/available
}

/**
 * AGC: Get features that would be unlocked by upgrading
 */
export function getUpgradeFeatures(
  currentTier: SubscriptionTier,
  targetTier: SubscriptionTier,
  tierConfigs?: Record<SubscriptionTier, TierConfig>
): string[] {
  const currentLimits = tierConfigs?.[currentTier]?.limits || DEFAULT_TIER_CONFIGS[currentTier].limits
  const targetLimits = tierConfigs?.[targetTier]?.limits || DEFAULT_TIER_CONFIGS[targetTier].limits
  
  const newFeatures: string[] = []
  
  // AGC: Check boolean features
  const booleanFeatures: (keyof TierLimits)[] = [
    'hasOfflineVoice',
    'hasWhatsAppIntegration',
    'hasAdvancedWeather',
    'hasYieldPotential',
    'hasDroidGrid',
    'hasSmartDroidGrid',
    'hasHumanAdvisor',
    'hasMultiFarm',
    'hasAdvancedAnalytics',
    'hasAPIAccess',
    'hasFullEMBRAPA',
    'hasEconomicsIntegration',
    'hasPrioritySupport',
    'hasExportCapabilities',
    'hasSatelliteImagery',
    'hasPrescriptionMaps',
  ]
  
  for (const feature of booleanFeatures) {
    if (!currentLimits[feature] && targetLimits[feature]) {
      newFeatures.push(feature)
    }
  }
  
  return newFeatures
}

/**
 * AGC: Calculate days until usage resets
 */
export function getDaysUntilReset(): number {
  const now = new Date()
  const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1)
  const diffTime = nextMonth.getTime() - now.getTime()
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
}

/**
 * AGC: Get reset date as formatted string
 */
export function getResetDateString(): string {
  const now = new Date()
  const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1)
  return nextMonth.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long' })
}

/**
 * AGC: Local storage keys for offline caching
 */
const SUBSCRIPTION_CACHE_KEY = 'droidfarm_subscription'
const USAGE_CACHE_KEY = 'droidfarm_usage'

/**
 * AGC: Cache subscription locally for offline access
 */
export function cacheSubscription(userId: string, subscription: UserSubscription): void {
  try {
    localStorage.setItem(`${SUBSCRIPTION_CACHE_KEY}_${userId}`, JSON.stringify(subscription))
  } catch (error) {
    console.error('❌ AGC: Error caching subscription:', error)
  }
}

/**
 * AGC: Get cached subscription
 */
export function getCachedSubscription(userId: string): UserSubscription | null {
  try {
    const cached = localStorage.getItem(`${SUBSCRIPTION_CACHE_KEY}_${userId}`)
    if (cached) {
      return JSON.parse(cached)
    }
  } catch (error) {
    console.error('❌ AGC: Error reading cached subscription:', error)
  }
  return null
}

/**
 * AGC: Cache usage locally for offline access
 */
export function cacheUsage(userId: string, usage: UsageTracker): void {
  try {
    localStorage.setItem(`${USAGE_CACHE_KEY}_${userId}_${usage.month}`, JSON.stringify(usage))
  } catch (error) {
    console.error('❌ AGC: Error caching usage:', error)
  }
}

/**
 * AGC: Get cached usage
 */
export function getCachedUsage(userId: string, month: string = getCurrentMonth()): UsageTracker | null {
  try {
    const cached = localStorage.getItem(`${USAGE_CACHE_KEY}_${userId}_${month}`)
    if (cached) {
      return JSON.parse(cached)
    }
  } catch (error) {
    console.error('❌ AGC: Error reading cached usage:', error)
  }
  return null
}

/**
 * AGC: Increment local usage (for offline mode)
 * AGC: Will be synced when back online
 */
export function incrementLocalUsage(userId: string, feature: LimitedFeature, amount: number = 1): UsageTracker {
  const month = getCurrentMonth()
  let usage = getCachedUsage(userId, month)
  
  if (!usage) {
    usage = {
      ...DEFAULT_USAGE,
      month,
      userId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
  }
  
  const { usageField } = getFeatureMapping(feature)
  ;(usage[usageField] as number) += amount
  usage.updatedAt = new Date().toISOString()
  
  cacheUsage(userId, usage)
  return usage
}

/**
 * AGC: Get subscription with fallback to cache
 */
export async function getSubscriptionWithFallback(userId: string): Promise<UserSubscription> {
  try {
    const subscription = await getUserSubscription(userId)
    cacheSubscription(userId, subscription)
    return subscription
  } catch (error) {
    console.log('⚠️ AGC: Using cached subscription due to error')
    const cached = getCachedSubscription(userId)
    if (cached) {
      return cached
    }
    // AGC: Return default if nothing cached
    return {
      ...DEFAULT_SUBSCRIPTION,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
  }
}

/**
 * AGC: Get usage with fallback to cache
 */
export async function getUsageWithFallback(userId: string): Promise<UsageTracker> {
  const month = getCurrentMonth()
  try {
    const usage = await getUsageForMonth(userId, month)
    cacheUsage(userId, usage)
    return usage
  } catch (error) {
    console.log('⚠️ AGC: Using cached usage due to error')
    const cached = getCachedUsage(userId, month)
    if (cached) {
      return cached
    }
    // AGC: Return default if nothing cached
    return {
      ...DEFAULT_USAGE,
      month,
      userId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
  }
}

/**
 * AGC: Fetch all tier configs from backend
 */
export async function fetchTierConfigs(): Promise<Record<SubscriptionTier, TierConfig>> {
  try {
    const response = await fetch(`${BACKEND_URL}/api/v1/tier-config`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    })

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`)
    }

    const data = await response.json()
    if (data.success && data.tiers) {
      return data.tiers as Record<SubscriptionTier, TierConfig>
    }
    throw new Error('Invalid tier config response')
  } catch (error) {
    console.error('❌ AGC: Error fetching tier configs:', error)
    throw error
  }
}

/**
 * AGC: Cache tier configs to localStorage
 */
export function cacheTierConfigs(configs: Record<SubscriptionTier, TierConfig>): void {
  try {
    localStorage.setItem(TIER_CONFIGS_CACHE_KEY, JSON.stringify(configs))
  } catch (error) {
    console.error('❌ AGC: Error caching tier configs:', error)
  }
}

/**
 * AGC: Get cached tier configs from localStorage
 */
export function getCachedTierConfigs(): Record<SubscriptionTier, TierConfig> | null {
  try {
    const cached = localStorage.getItem(TIER_CONFIGS_CACHE_KEY)
    if (cached) return JSON.parse(cached)
  } catch (error) {
    console.error('❌ AGC: Error reading cached tier configs:', error)
  }
  return null
}

/**
 * AGC: Get tier configs with fallback: API → localStorage → defaults
 */
export async function getTierConfigsWithFallback(): Promise<Record<SubscriptionTier, TierConfig>> {
  try {
    const configs = await fetchTierConfigs()
    cacheTierConfigs(configs)
    return configs
  } catch (error) {
    console.log('⚠️ AGC: Using cached tier configs due to error')
    const cached = getCachedTierConfigs()
    if (cached) return cached
    return { ...DEFAULT_TIER_CONFIGS }
  }
}

export default {
  getUserSubscription,
  updateSubscription,
  getUsageForMonth,
  incrementUsage,
  checkLimit,
  checkAllLimits,
  hasFeature,
  getUpgradeFeatures,
  getDaysUntilReset,
  getResetDateString,
  getCurrentMonth,
  cacheSubscription,
  getCachedSubscription,
  cacheUsage,
  getCachedUsage,
  incrementLocalUsage,
  getSubscriptionWithFallback,
  getUsageWithFallback,
  fetchTierConfigs,
  cacheTierConfigs,
  getCachedTierConfigs,
  getTierConfigsWithFallback,
}

